/**
 * useGenerateImage Hook
 * Mutation hook for AI image generation with abort support and toast notifications
 */

import { useMutation } from "@tanstack/react-query"
import { useRef } from "react"
import { toast } from "sonner"
import {
  type GenerateImagesRequest,
  type GenerateImagesResponse,
  generateImages,
} from "../services/ai-image.service"

export function useGenerateImage() {
  const abortControllerRef = useRef<AbortController | null>(null)

  const mutation = useMutation<GenerateImagesResponse, Error, GenerateImagesRequest>({
    mutationFn: (data: GenerateImagesRequest) => {
      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()
      return generateImages(data, abortControllerRef.current.signal)
    },
    onSuccess: data => {
      if (data.warning) {
        toast.warning(data.warning)
      }
    },
    onError: error => {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        error.message ||
        "Image generation failed"
      toast.error(message)
    },
  })

  const cancel = () => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
  }

  return {
    ...mutation,
    cancel,
  }
}
